import React, { useMemo, useRef, useState } from "react";
import Icon from "../shared/Icon";

/**
 * Sélection d'un résident déjà connu du point de collecte.
 *
 * C'est le chemin le plus court de l'écran : l'annuaire est chargé au montage,
 * donc imprimer l'étiquette de quelqu'un ne demande qu'une recherche et un clic.
 * L'import CSV et la saisie manuelle restent là pour les cas que l'annuaire ne
 * couvre pas — un nouvel arrivant, un résident sans commande.
 *
 * Combobox plutôt que `<select>` : un point de collecte peut compter plus de
 * cent résidents, et une liste déroulante native n'y est pas cherchable.
 */
const ResidentPicker = ({ residents, alreadyAdded, onPick, isLoading, t }) => {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const blurTimer = useRef(null);

  const matches = useMemo(() => {
    const term = query.trim().toLowerCase();
    const available = residents.filter((resident) => !alreadyAdded.has(resident.woshId));
    if (!term) return available.slice(0, 50);

    return available
      .filter((resident) => {
        const haystack = `${resident.firstName} ${resident.lastName} ${resident.woshId}`;
        return haystack.toLowerCase().includes(term);
      })
      .slice(0, 50);
  }, [residents, alreadyAdded, query]);

  const choose = (resident) => {
    onPick(resident);
    setQuery("");
    setIsOpen(false);
  };

  // Le clic sur une option passe par un blur : on diffère la fermeture d'un
  // tick, sinon la liste disparaît avant que le clic n'aboutisse.
  const handleBlur = () => {
    blurTimer.current = setTimeout(() => setIsOpen(false), 120);
  };

  const handleFocus = () => {
    if (blurTimer.current) clearTimeout(blurTimer.current);
    setIsOpen(true);
  };

  if (isLoading) {
    return (
      <div className="mgr-lbl-picker">
        <div className="mgr-skel" style={{ height: 42, borderRadius: 11 }} />
      </div>
    );
  }

  if (residents.length === 0) return null;

  return (
    <div className="mgr-lbl-picker">
      <label className="mgr-lbl-picker-field">
        <span className="mgr-lbl-picker-label">{t("Select a resident")}</span>
        <span className="mgr-lbl-picker-input">
          <Icon name="search" style={{ fontSize: 17, color: "var(--mgr-brand)" }} />
          <input
            type="search"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setIsOpen(true);
            }}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={t("Search by name or user ID")}
            aria-expanded={isOpen}
            aria-controls="mgr-lbl-picker-list"
            role="combobox"
          />
        </span>
      </label>

      {isOpen ? (
        <ul className="mgr-lbl-picker-list" id="mgr-lbl-picker-list" role="listbox">
          {matches.length === 0 ? (
            <li className="mgr-lbl-picker-empty">{t("No resident matches.")}</li>
          ) : (
            matches.map((resident) => (
              <li key={resident.woshId}>
                <button
                  type="button"
                  className="mgr-lbl-picker-option"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => choose(resident)}
                >
                  <span className="mgr-lbl-picker-name">
                    {[resident.firstName, resident.lastName].filter(Boolean).join(" ")}
                  </span>
                  <span className="mgr-woshid mgr-woshid--sm">{resident.woshId}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
};

export default ResidentPicker;
